import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import SEOHelmet from '../../components/SEOHelmet';
import { ArticleSchema, BreadcrumbSchema } from '../../components/SchemaMarkup';
import { blogSeoDdata } from '../../data/seoData';
import { blogArticles } from '../../data/blogArticles';
import { blogPosts } from '../../data/blogPosts';
import { FaArrowLeft, FaShare } from 'react-icons/fa';
import './BlogPost.css';

const slugify = (text) =>
  text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

const BlogPost = () => {
  const { slug } = useParams();
  const [shareMessage, setShareMessage] = useState('');
  const [progress, setProgress] = useState(0);

  const post = blogArticles[slug];

  const headings = useMemo(
    () => (post?.content || [])
      .filter((s) => s.type === 'heading')
      .map((s) => ({ id: slugify(s.text), text: s.text })),
    [post]
  );

  const related = useMemo(() => {
    if (!post) return [];
    return blogPosts
      .filter((p) => p.slug !== slug)
      .sort((a, b) => {
        const score = (x) => (x.destination === post.destination ? 2 : 0) +
          (x.category === post.category ? 1 : 0);
        return score(b) - score(a);
      })
      .slice(0, 3);
  }, [post, slug]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  useEffect(() => {
    const onScroll = () => {
      const max = document.documentElement.scrollHeight - window.innerHeight;
      setProgress(max > 0 ? Math.min(100, (window.scrollY / max) * 100) : 0);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, [slug]);

  const handleShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({ title: post.title, text: post.excerpt, url: window.location.href });
        setShareMessage('Thanks for sharing');
      } else {
        await navigator.clipboard.writeText(window.location.href);
        setShareMessage('Link copied');
      }
      setTimeout(() => setShareMessage(''), 3000);
    } catch (err) {
      if (err.name !== 'AbortError') {
        setShareMessage('Unable to share');
        setTimeout(() => setShareMessage(''), 3000);
      }
    }
  };

  if (!post) {
    return (
      <div className="article-missing">
        <div className="container">
          <h1>We can&rsquo;t find that story</h1>
          <p>It may have moved. Everything we&rsquo;ve published is on the blog index.</p>
          <Link to="/blog" className="btn btn-primary">
            <FaArrowLeft aria-hidden="true" /> Back to the blog
          </Link>
        </div>
      </div>
    );
  }

  const seo = blogSeoDdata[slug] || {};

  const renderBlock = (section, index) => {
    switch (section.type) {
      case 'heading':
        return <h2 key={index} id={slugify(section.text)}>{section.text}</h2>;
      case 'paragraph':
        return <p key={index}>{section.text}</p>;
      case 'list':
        return (
          <ul key={index}>
            {section.items.map((item) => <li key={item}>{item}</li>)}
          </ul>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <SEOHelmet
        title={seo.title || `${post.title} | Safarnamah`}
        description={seo.description || post.excerpt}
        keywords={seo.keywords || `${post.title}, safarnamah, travel guide`}
        image={post.image}
        url={`https://safarnamah.in/blog/${slug}`}
        type="article"
      />
      <ArticleSchema
        title={post.title}
        description={post.excerpt}
        image={post.image}
        url={`https://safarnamah.in/blog/${slug}`}
        datePublished={post.date}
        dateModified={post.date}
        author={post.author}
      />
      <BreadcrumbSchema items={[
        { name: 'Home', url: 'https://safarnamah.in' },
        { name: 'Blog', url: 'https://safarnamah.in/blog' },
        { name: post.title, url: `https://safarnamah.in/blog/${slug}` },
      ]} />

      <div className="article-page">
        <div className="read-progress" style={{ transform: `scaleX(${progress / 100})` }} />

        <header className="article-hero">
          <div className="article-hero-bg" style={{ backgroundImage: `url(${post.image})` }} />
          <div className="article-hero-scrim" />
          <div className="container article-hero-in">
            <Link to="/blog" className="back-link">
              <FaArrowLeft aria-hidden="true" /> The blog
            </Link>
            <motion.div
              initial={{ opacity: 0, y: 34 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
            >
              <span className="eyebrow">{post.category} &nbsp;&middot;&nbsp; {post.destination}</span>
              <h1>{post.title}</h1>
              <p className="article-standfirst">{post.excerpt}</p>
              <div className="byline">
                <span>{post.author}</span>
                <time>{post.date}</time>
                <span>{post.readTime}</span>
              </div>
            </motion.div>
          </div>
        </header>

        <div className="article-body">
          <div className="container article-grid">
            <motion.article
              className="article-main prose"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6 }}
            >
              {post.content.map(renderBlock)}
            </motion.article>

            <aside className="article-aside">
              {headings.length > 1 && (
                <nav className="article-nav" aria-label="In this story">
                  <span className="aside-label">In this story</span>
                  <ul>
                    {headings.map((h) => (
                      <li key={h.id}><a href={`#${h.id}`}>{h.text}</a></li>
                    ))}
                  </ul>
                </nav>
              )}

              <div className="aside-card">
                <h3>Written by</h3>
                <p>
                  Saket &amp; Pallavi. We publish guides only for places we&rsquo;ve
                  actually been.
                </p>
                <button type="button" className="article-share" onClick={handleShare}>
                  <FaShare aria-hidden="true" /> Share this story
                </button>
                {shareMessage && <span className="article-share-msg">{shareMessage}</span>}
              </div>
            </aside>
          </div>
        </div>

        {related.length > 0 && (
          <section className="related">
            <div className="container">
              <h2>Keep reading</h2>
              <div className="related-grid">
                {related.map((r) => (
                  <Link className="related-card" to={`/blog/${r.slug}`} key={r.slug}>
                    <div className="related-photo" style={{ backgroundImage: `url(${r.image})` }} />
                    <span className="kicker">{r.destination}</span>
                    <h3>{r.title}</h3>
                    <span className="related-meta">{r.readTime}</span>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </div>
    </>
  );
};

export default BlogPost;
